import { RefObject, useEffect, useState } from 'react';

type ActiveTimeCell = {
	value: string;
	top: number;
	bottom: number;
};

export function getActiveTimeLabelValue({
	cells,
	boundaryTop,
}: {
	cells: ActiveTimeCell[];
	boundaryTop: number;
}) {
	if (!cells.length) return null;

	const candidates = cells.filter(
		({ top, bottom }) => top <= boundaryTop && bottom >= boundaryTop
	);

	if (candidates.length) {
		const chosen = [...candidates].sort(
			(a, b) => Math.abs(a.top - boundaryTop) - Math.abs(b.top - boundaryTop)
		)[0];
		return chosen.value || null;
	}

	const next = [...cells]
		.map((cell) => ({
			value: cell.value,
			delta: cell.top - boundaryTop,
		}))
		.filter((item) => item.delta >= 0)
		.sort((a, b) => a.delta - b.delta)[0];

	return next?.value || cells[0].value || null;
}

export function useActiveTimeLabel({
	containerRef,
	enabled,
}: {
	containerRef: RefObject<HTMLElement>;
	enabled: boolean;
}) {
	const [activeTime, setActiveTime] = useState<string | null>(null);

	useEffect(() => {
		if (!enabled || !containerRef.current) {
			setActiveTime(null);
			return;
		}

		const container = containerRef.current;
		const header = container.querySelector<HTMLElement>('[data-sticky-header]');
		const updateActiveTime = () => {
			if (container.scrollTop <= 0) {
				setActiveTime(null);
				return;
			}

			const timeCells = Array.from(
				container.querySelectorAll<HTMLElement>('[data-time-label]')
			).map((cell) => {
				const rect = cell.getBoundingClientRect();
				return {
					value: cell.dataset.timeLabel ?? '',
					top: rect.top,
					bottom: rect.bottom,
				};
			});
			if (!timeCells.length) {
				setActiveTime(null);
				return;
			}

			const boundaryTop = header
				? header.getBoundingClientRect().bottom
				: container.getBoundingClientRect().top;

			setActiveTime(getActiveTimeLabelValue({ cells: timeCells, boundaryTop }));
		};

		updateActiveTime();
		container.addEventListener('scroll', updateActiveTime, { passive: true });
		return () => container.removeEventListener('scroll', updateActiveTime);
	}, [containerRef, enabled]);

	return activeTime;
}
